import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaSpinner, FaXmark } from 'react-icons/fa6';

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Texto extra pesquisável/exibido (ex.: nome do estado ao lado da sigla). */
  hint?: string;
}

interface SearchSelectInputProps {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  requiredMark?: boolean;
  errorMessage?: string;
  emptyMessage?: string;
  /** Limite de itens renderizados (listas grandes como municípios de SP). */
  maxItems?: number;
}

const normalize = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Campo digitável com lista suspensa (busca sem acento, seta abre a lista
 * completa, X limpa) — mesmo comportamento do BrasilUfMunicipioSearchFields
 * do cadastro de cliente do NexxusOne.
 */
export const SearchSelectInput: React.FC<SearchSelectInputProps> = ({
  name,
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  loading = false,
  requiredMark = false,
  errorMessage,
  emptyMessage = 'Nenhum resultado encontrado',
  maxItems = 200,
}) => {
  const listId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  /** Aberto pela seta/foco: lista completa, sem filtrar pelo texto já selecionado. */
  const [showAll, setShowAll] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const selected = options.find((o) => o.value === value);
  const selectedText = selected ? (selected.hint ? `${selected.label} — ${selected.hint}` : selected.label) : value;
  const inputText = open && !showAll ? search : selectedText;

  const filtered = useMemo(() => {
    const term = showAll ? '' : normalize(search);
    const list = term
      ? options.filter((o) => normalize(`${o.label} ${o.hint ?? ''}`).includes(term))
      : options;
    return list.slice(0, maxItems);
  }, [options, search, showAll, maxItems]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const openList = (all: boolean) => {
    if (disabled) return;
    setShowAll(all);
    setSearch('');
    const idx = all ? Math.max(0, options.findIndex((o) => o.value === value)) : 0;
    setHighlight(idx);
    setOpen(true);
  };

  const select = (opt: SearchSelectOption) => {
    onChange(opt.value);
    setOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      openList(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) select(filtered[highlight]);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="flex flex-col" ref={containerRef}>
      <label htmlFor={`${listId}-input`} className="text-sm font-medium text-gray-600 flex items-center gap-1">
        {label} {requiredMark && <span className="text-red-500">*</span>}
        {loading && <FaSpinner className="animate-spin w-3 h-3 text-sky-500" />}
      </label>
      <div className="relative mt-1">
        <input
          id={`${listId}-input`}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${listId}-list`}
          aria-autocomplete="list"
          autoComplete="off"
          value={inputText}
          placeholder={loading ? 'Carregando...' : placeholder}
          disabled={disabled}
          onFocus={() => !open && openList(true)}
          onClick={() => !open && openList(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowAll(false);
            setHighlight(0);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className={`w-full h-[42px] px-4 pr-16 rounded-lg border bg-white truncate ${
            errorMessage ? 'border-red-500' : 'border-gray-300'
          } focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed`}
        />
        <div className="absolute inset-y-0 right-2 flex items-center gap-1">
          {value && !disabled && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={`Limpar ${label}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange('');
                setSearch('');
                setOpen(false);
              }}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <FaXmark className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Abrir lista de ${label}`}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => (open ? setOpen(false) : openList(true))}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed"
          >
            <FaChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {open && (
          <ul
            id={`${listId}-list`}
            ref={listRef}
            role="listbox"
            className="absolute z-30 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1"
          >
            {filtered.length === 0 ? (
              <li className="px-4 py-2 text-sm text-gray-500">{loading ? 'Carregando...' : emptyMessage}</li>
            ) : (
              filtered.map((opt, i) => (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === value}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => select(opt)}
                  className={`px-4 py-2 text-sm cursor-pointer ${
                    i === highlight ? 'bg-sky-50' : ''
                  } ${opt.value === value ? 'font-semibold text-sky-700' : 'text-gray-700'}`}
                >
                  {opt.label}
                  {opt.hint && <span className="text-gray-400"> — {opt.hint}</span>}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
      {errorMessage && <p className="text-red-500 text-sm mt-1">{errorMessage}</p>}
    </div>
  );
};
